import axios from "axios";
import { getApiBaseUrl } from "../utils/urlHandler";

/**
 * LinkedIn PDF pages as pictures (SGL-573): POST /content/{customer_id}/document-pages.
 * content-management draws each page once and caches it next to the PDF; this asks
 * for those pictures so the review page can show the document the way LinkedIn will.
 */

export interface DocumentPage {
  url: string;
  width: number;
  height: number;
}

/** One PDF's answer: its pages, or why there are none. */
export interface DocumentPagesResult {
  url: string;
  page_count?: number;
  pages?: DocumentPage[];
  truncated?: boolean;
  /** not_supported | missing | too_large | unreadable | failed | retry */
  error?: string;
}

export const fetchDocumentPages = async (
  customerId: string,
  urls: string[],
  idToken: string
): Promise<DocumentPagesResult[]> => {
  // A plain instance, without the app's global interceptors: the pictures are
  // optional (the PDF tile is the fallback), so a 403 from a backend that does not
  // have this route yet must never raise the "Sign in required" dialog. The token
  // is refreshed here instead, as the interceptor would. Imported on use: the
  // Cognito module builds its user pool on import, and the review card (which
  // imports this) must stay importable without Cognito settings (its tests).
  const { getFreshIdToken } = await import("../auth/getFreshIdToken");
  const token = await getFreshIdToken(idToken);
  const response = await axios.create().post(
    `${getApiBaseUrl("REACT_APP_CONTENT_API_URL")}/${encodeURIComponent(customerId)}/document-pages`,
    { urls },
    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
  );
  const documents = (response.data as { documents?: unknown })?.documents;
  return Array.isArray(documents) ? (documents as DocumentPagesResult[]) : [];
};
